import React, { useEffect, useState } from "react";
import { X, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/xwvrzgkg";

export const EstimateModal = ({ isOpen, onClose }) => {
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
    source: "estimate-modal",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
      document.body.style.overflow = "hidden";
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const resetForm = () => {
    setFormData({
      name: "",
      phone: "",
      email: "",
      service: "",
      message: "",
      source: "estimate-modal",
    });
  };

  const closeModal = () => {
    if (isSubmitting) return;

    setIsClosing(true);

    setTimeout(() => {
      setShouldRender(false);
      setIsClosing(false);
      document.body.style.overflow = "";
      onClose();
    }, 300);
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        toast({
          title: "Message sent!",
          description:
            "Your estimate request was sent successfully. We will contact you soon.",
        });

        resetForm();
        closeModal();
      } else {
        toast({
          title: "Submission failed",
          description:
            result?.errors?.[0]?.message ||
            "Something went wrong. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Network error",
        description:
          "We could not send your message right now. Please try again or call us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!shouldRender) return null;

  return (
    <div
      className={`estimate-modal-overlay ${
        isClosing ? "modal-closing" : "modal-opening"
      }`}
      onClick={closeModal}
    >
      <div
        className={`estimate-modal-box ${
          isClosing ? "modal-box-closing" : "modal-box-opening"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="estimate-modal-close"
          onClick={closeModal}
          disabled={isSubmitting}
        >
          <X className="h-6 w-6" />
        </button>

        <h2 className="estimate-modal-title">Send Us a Message</h2>

        <form onSubmit={handleSubmit} className="estimate-modal-form">
          <input type="hidden" name="source" value={formData.source} />

          <div>
            <Label htmlFor="modal-name">Name *</Label>
            <Input
              id="modal-name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Your full name"
              className="mt-2 estimate-input"
            />
          </div>

          <div>
            <Label htmlFor="modal-phone">Phone *</Label>
            <Input
              id="modal-phone"
              name="phone"
              type="tel"
              required
              value={formData.phone}
              onChange={handleChange}
              placeholder="(206) 555-0123"
              className="mt-2 estimate-input"
            />
          </div>

          <div>
            <Label htmlFor="modal-email">Email *</Label>
            <Input
              id="modal-email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
              className="mt-2 estimate-input"
            />
          </div>

          <div>
            <Label htmlFor="modal-service">Service Needed</Label>
            <Input
              id="modal-service"
              name="service"
              type="text"
              value={formData.service}
              onChange={handleChange}
              placeholder="e.g., Landscaping, Pavers, Artificial Grass"
              className="mt-2 estimate-input"
            />
          </div>

          <div>
            <Label htmlFor="modal-message">Message *</Label>
            <Textarea
              id="modal-message"
              name="message"
              required
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us about your project..."
              rows={5}
              className="mt-2 estimate-textarea"
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-green-500 hover:bg-green-600 text-black font-bold text-lg py-6 rounded-xl"
          >
            {isSubmitting ? (
              "Sending..."
            ) : (
              <>
                Send Message
                <Send className="ml-2 h-5 w-5" />
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default EstimateModal;
